import { Component, Host, h, Prop, Event, Watch, EventEmitter, Element, Method, AttachInternals } from '@stencil/core';
import 'element-internals-polyfill';

let id = 0;

/**
 * @slot - Radio text content
 *
 * @part label - The component's label wrapper element.
 * @part input - The component's input element.
 * @part text - The component's text content element.
 */
@Component({
  tag: 'o2-radio',
  styleUrl: 'radio.scss',
  shadow: true,
  formAssociated: true,
})
export class O2Radio {
  private input?: HTMLInputElement;
  private inputId = `o2-radio-${++id}`;
  private labelId = `${this.inputId}-label`;

  private hasTextSlot: boolean;

  @AttachInternals() internals: ElementInternals;

  @Element() el!: HTMLO2RadioElement;

  /**
   * Watches disabled property
   */
  @Watch('disabled')
  handleDisabledChange() {
    this.internals.ariaDisabled = this.disabled.toString();
  }

  /**
   * Sets the value of radio.
   */
  @Prop({ mutable: true, reflect: true }) value: string;

  /**
   * Sets the name of radio input.
   */
  @Prop() name?: string = this.inputId;

  /**
   * turns on/off the checked state of radio.
   */
  @Prop({ mutable: true, reflect: true }) checked?: boolean = false;

  /**
   * Disables radio.
   */
  @Prop({ reflect: true }) disabled?: boolean = false;

  /**
   * If `true`, the user must fill in a value before submitting a form.
   */
  @Prop() required? = false;

  /**
   * Indicates error
   */
  @Prop({
    reflect: true,
  })
  error?: boolean = false;

  /**
   * Emits radio change.
   */
  @Event() o2RadioChange!: EventEmitter<any>;

  /**
   * Sets focus on the native `input` in `o2-radio`. Use this method instead of the global
   * `input.focus()`.
   */
  @Method()
  async setFocus() {
    if (this.input) {
      this.input.focus();
    }
  }

  /**
   * Sets blur on the native `input` in `o2-radio`. Use this method instead of the global
   * `input.blur()`.
   * @internal
   */
  @Method()
  async setBlur() {
    if (this.input) {
      this.input.blur();
    }
  }

  /**
   * Fires on component change
   */
  private onChange = () => {
    this.value = this.el.shadowRoot.querySelector('input').value;
    this.internals.ariaChecked = this.checked.toString();

    this.o2RadioChange.emit({
      value: this.value == null ? this.value : this.value.toString(),
    });
  };

  connectedCallback() {
    // check if wc has at least one node
    this.hasTextSlot = !!this.el.childNodes.length;
  }

  componentWillLoad() {
    this.internals.ariaChecked = this.checked.toString();
    this.internals.ariaDisabled = this.disabled.toString();
    this.internals.ariaRequired = this.required.toString();
    this.internals.role = 'radio';
  }

  render() {
    return (
      <Host>
        <div class={`radio ${this.disabled ? 'radio--disabled' : ''} ${this.checked ? 'radio--checked' : ''}`}>
          <label htmlFor={this.inputId} id={this.labelId} class="radio__label" part="label">
            <input
              ref={el => (this.input = el)}
              id={this.inputId}
              class={`radio__input ${this.error ? 'radio__input--error' : ''} ${this.disabled ? 'radio__input--disabled' : ''}`}
              name={this.name}
              type="radio"
              value={this.value}
              disabled={this.disabled}
              onChange={this.onChange}
              checked={this.checked}
              part="input"
              role="radio"
              aria-checked={`${this.checked}`}
              aria-disabled={this.disabled}
              aria-hidden={this.disabled ? 'true' : null}
              aria-labelledby={this.labelId ? this.labelId : null}
            />
            {this.hasTextSlot && (
              <div class="radio__text" part="text">
                <slot></slot>
              </div>
            )}
          </label>
        </div>
      </Host>
    );
  }
}
